import { type CatalogPermissionKey } from '@aysar/contracts';

/**
 * A permission key goes into the i18next path VERBATIM, dots included.
 *
 * i18next reads `.` as a path separator and every permission key contains one
 * (`verification.review`), so this looks like it needs escaping — and P9 escaped
 * it as `\.` on the belief that a backslash is i18next's `keySeparator` escape.
 * **There is no such feature.** `deepFind` splits the path on `.` with no
 * backslash awareness, so the escaped form matched no property and every label
 * rendered as `key.billing\.manage.label` in the role editor.
 *
 * The unescaped path is the one that works, and it works BECAUSE of how
 * `deepFind` misses: after splitting, it re-joins its tokens progressively and
 * finds the flat `"billing.manage"` property that the catalogue actually
 * declares. So the copy stays addressable by the literal key a developer can
 * grep for, which is what the catalogue shape was chosen for — no mangled
 * `verification_review`, and no nesting either.
 *
 * `permissions.util.spec.ts` resolves every catalogue key through the real
 * instance rather than asserting the built string, because a shape assertion is
 * exactly what let the escaped version ship green.
 */
const NAMESPACE = 'permissions';

/** The translated name of one permission, e.g. "Review verification cases". */
export function permissionLabelKey(key: CatalogPermissionKey): string {
  return `${NAMESPACE}:key.${key}.label`;
}

/** What holding the permission actually lets a person do — shown under the label. */
export function permissionDescriptionKey(key: CatalogPermissionKey): string {
  return `${NAMESPACE}:key.${key}.description`;
}

/**
 * The heading of a resource group in the role editor.
 *
 * `permissionsByResource()` returns the bare global wildcard (`manage`) under
 * the empty-string resource; it gets its own copy rather than an empty heading,
 * because it is the single most consequential row in the checklist.
 *
 * A resource can itself be dotted — `permissionResource` cuts at the LAST dot,
 * so the derived `integrations.<capability>.manage` keys group under
 * `integrations.ai-text` and friends. Those six headings were the silent half of
 * the escaping bug: their copy shipped in both locales and was unreachable,
 * while the single-word resources (`billing`, `courses`) happened to work
 * because escaping a string with no dots is a no-op.
 */
export function permissionResourceLabelKey(resource: string): string {
  return resource === '' ? `${NAMESPACE}:resource.global` : `${NAMESPACE}:resource.${resource}`;
}
